import { ProductCard } from '@/components/catalog/ProductCard';
import { CatalogPagination } from '@/components/catalog/CatalogPagination';
import { api } from '@/lib/api';
import { productCatalogId } from '@/lib/catalog';
import { MOCK_PRODUCTOS } from '@/data/mockCatalog';
import { CATEGORIAS_DEFAULT } from '@/types';
import type { Producto } from '@/types';
import { CatalogBanner } from '@/components/layout/CatalogBanner';
import { Chip, EmptyState, SkeletonGrid } from '@/components/ui';
import {
  ArrowUpDown,
  Check,
  Package,
  Search,
  SlidersHorizontal,
  X,
} from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { cn } from '@/lib/utils';

type OrdenCatalogo =
  | 'relevancia'
  | 'precio_asc'
  | 'precio_desc'
  | 'nombre_asc'
  | 'nombre_desc'
  | 'stock_desc';

const PAGE_SIZE = 24;

const ORDEN_OPTIONS: { value: OrdenCatalogo; label: string; hint: string }[] = [
  { value: 'relevancia', label: 'Relevancia', hint: 'Mejor coincidencia' },
  { value: 'precio_asc', label: 'Precio: menor', hint: 'Más barato primero' },
  { value: 'precio_desc', label: 'Precio: mayor', hint: 'Más caro primero' },
  { value: 'nombre_asc', label: 'Nombre A–Z', hint: 'Alfabético' },
  { value: 'nombre_desc', label: 'Nombre Z–A', hint: 'Alfabético inverso' },
  { value: 'stock_desc', label: 'Más disponibles', hint: 'Mayor existencia' },
];

function parseOrden(raw: string | null, hasQ: boolean): OrdenCatalogo {
  const v = (raw ?? '') as OrdenCatalogo;
  if (ORDEN_OPTIONS.some((o) => o.value === v)) return v;
  return hasQ ? 'relevancia' : 'nombre_asc';
}

export function CatalogPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const categoria = searchParams.get('categoria') ?? '';
  const qParam = searchParams.get('q') ?? '';
  const soloStock = searchParams.get('stock') === '1';
  const orden = parseOrden(searchParams.get('orden'), !!qParam);
  const page = Math.max(1, parseInt(searchParams.get('page') || '1', 10) || 1);

  const [qInput, setQInput] = useState(qParam);
  const [productos, setProductos] = useState<Producto[]>([]);
  const [total, setTotal] = useState(0);
  const [pages, setPages] = useState(1);
  const [loading, setLoading] = useState(true);
  const [filtersOpen, setFiltersOpen] = useState(false);

  useEffect(() => {
    setQInput(qParam);
  }, [qParam]);

  useEffect(() => {
    const t = window.setTimeout(() => {
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          const trimmed = qInput.trim();
          if (trimmed) next.set('q', trimmed);
          else next.delete('q');
          if ((next.get('q') ?? '') === (prev.get('q') ?? '')) return prev;
          // Nueva búsqueda → relevancia + página 1
          if (trimmed) next.set('orden', 'relevancia');
          next.delete('page');
          return next;
        },
        { replace: true },
      );
    }, 320);
    return () => window.clearTimeout(t);
  }, [qInput, setSearchParams]);

  useEffect(() => {
    let alive = true;
    setLoading(true);
    api
      .productos({
        categoria: categoria || undefined,
        q: qParam || undefined,
        orden,
        solo_stock: soloStock || undefined,
        page,
        limit: PAGE_SIZE,
      })
      .then((res) => {
        if (!alive) return;
        const tot = res.total ?? res.items.length;
        setProductos(res.items);
        setTotal(tot);
        setPages(
          res.pages ??
            Math.max(1, Math.ceil(tot / (res.limit ?? PAGE_SIZE))),
        );
      })
      .catch(() => {
        if (!alive) return;
        setProductos(MOCK_PRODUCTOS);
        setTotal(MOCK_PRODUCTOS.length);
        setPages(1);
      })
      .finally(() => {
        if (alive) setLoading(false);
      });
    return () => {
      alive = false;
    };
  }, [categoria, qParam, orden, soloStock, page]);

  const patchParams = (patch: Record<string, string | null>, resetPage = true) => {
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      Object.entries(patch).forEach(([k, v]) => {
        if (v === null || v === '') next.delete(k);
        else next.set(k, v);
      });
      if (resetPage && !('page' in patch)) next.delete('page');
      return next;
    });
  };

  const goPage = (nextPage: number) => {
    const p = Math.max(1, nextPage);
    patchParams({ page: p <= 1 ? null : String(p) }, false);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const filtrarCategoria = (slug: string) => {
    patchParams({ categoria: slug || null });
    setFiltersOpen(false);
  };

  const setOrden = (value: OrdenCatalogo) => {
    patchParams({ orden: value });
  };

  const toggleStock = () => {
    patchParams({ stock: soloStock ? null : '1' });
  };

  const limpiarFiltros = () => {
    setQInput('');
    setSearchParams(new URLSearchParams());
    setFiltersOpen(false);
  };

  const catLabel =
    CATEGORIAS_DEFAULT.find((c) => c.slug === categoria)?.nombre ?? null;

  const ordenLabel = ORDEN_OPTIONS.find((o) => o.value === orden)?.label ?? 'Orden';

  const activeFilters = useMemo(() => {
    const chips: { key: string; label: string; clear: () => void }[] = [];
    if (catLabel) {
      chips.push({
        key: 'cat',
        label: catLabel,
        clear: () => filtrarCategoria(''),
      });
    }
    if (qParam) {
      chips.push({
        key: 'q',
        label: `“${qParam}”`,
        clear: () => {
          setQInput('');
          patchParams({ q: null });
        },
      });
    }
    if (soloStock) {
      chips.push({
        key: 'stock',
        label: 'Con existencia',
        clear: () => patchParams({ stock: null }),
      });
    }
    if (orden !== (qParam ? 'relevancia' : 'nombre_asc')) {
      chips.push({
        key: 'orden',
        label: ordenLabel,
        clear: () =>
          patchParams({ orden: qParam ? 'relevancia' : 'nombre_asc' }),
      });
    }
    return chips;
  }, [catLabel, qParam, soloStock, orden, ordenLabel]);

  const FiltersBody = (
    <>
      <div>
        <p className="text-xs font-semibold uppercase tracking-wide text-surface-500">
          Categoría
        </p>
        <div className="mt-3 flex flex-col gap-1">
          <Chip active={!categoria} onClick={() => filtrarCategoria('')} className="justify-start">
            Todas
          </Chip>
          {CATEGORIAS_DEFAULT.map((cat) => (
            <Chip
              key={cat.slug}
              active={categoria === cat.slug}
              onClick={() => filtrarCategoria(cat.slug)}
              className="justify-start capitalize"
            >
              {cat.nombre}
            </Chip>
          ))}
        </div>
      </div>

      <div className="mt-6 border-t border-surface-200 pt-5">
        <p className="text-xs font-semibold uppercase tracking-wide text-surface-500">
          Disponibilidad
        </p>
        <button
          type="button"
          onClick={toggleStock}
          className={cn(
            'mt-3 flex w-full items-center justify-between rounded-lg border px-3 py-2.5 text-left text-sm transition-colors',
            soloStock
              ? 'border-brand-600 bg-brand-50 text-brand-800'
              : 'border-surface-200 bg-white text-surface-700 hover:bg-surface-50',
          )}
        >
          <span className="font-medium">Solo con existencia</span>
          <span
            className={cn(
              'flex h-5 w-5 items-center justify-center rounded border',
              soloStock
                ? 'border-brand-600 bg-brand-600 text-white'
                : 'border-surface-300 bg-white',
            )}
          >
            {soloStock ? <Check className="h-3.5 w-3.5" strokeWidth={3} /> : null}
          </span>
        </button>
      </div>

      <div className="mt-6 border-t border-surface-200 pt-5">
        <p className="text-xs font-semibold uppercase tracking-wide text-surface-500">
          Ordenar por
        </p>
        <div className="mt-3 flex flex-col gap-1">
          {ORDEN_OPTIONS.map((opt) => {
            // Relevancia solo tiene sentido con búsqueda
            if (opt.value === 'relevancia' && !qParam && !qInput.trim()) {
              return null;
            }
            return (
              <button
                key={opt.value}
                type="button"
                onClick={() => {
                  setOrden(opt.value);
                  setFiltersOpen(false);
                }}
                className={cn(
                  'flex items-start justify-between rounded-lg px-3 py-2 text-left transition-colors',
                  orden === opt.value
                    ? 'bg-brand-50 text-brand-800'
                    : 'text-surface-700 hover:bg-surface-50',
                )}
              >
                <span>
                  <span className="block text-sm font-medium">{opt.label}</span>
                  <span className="block text-[11px] text-surface-500">{opt.hint}</span>
                </span>
                {orden === opt.value ? (
                  <Check className="mt-0.5 h-4 w-4 shrink-0 text-brand-600" />
                ) : null}
              </button>
            );
          })}
        </div>
      </div>

      <p className="mt-6 border-t border-surface-200 pt-4 text-xs text-surface-500">
        {loading
          ? 'Cargando…'
          : `${total.toLocaleString('es-MX')} resultados · pág. ${page}/${Math.max(pages, 1)}`}
      </p>

      {activeFilters.length > 0 && (
        <button type="button" onClick={limpiarFiltros} className="btn-ghost mt-3 w-full text-sm">
          Limpiar filtros
        </button>
      )}
    </>
  );

  return (
    <>
      <CatalogBanner
        title={catLabel ?? (qParam ? `“${qParam}”` : 'Catálogo')}
        subtitle={
          qParam
            ? 'Resultados ordenados y listos para filtrar.'
            : 'Filtra por línea, existencia u ordena por precio.'
        }
        breadcrumb={[
          { label: 'Catálogo', to: catLabel || qParam ? '/catalogo' : undefined },
          ...(catLabel ? [{ label: catLabel }] : []),
          ...(qParam && !catLabel ? [{ label: 'Búsqueda' }] : []),
        ]}
      />

      <div className="page-section !pt-4 sm:!pt-8">
        <div className="container-app">
          <div className="grid gap-6 lg:grid-cols-[240px_1fr] xl:grid-cols-[260px_1fr]">
            {/* Sidebar desktop */}
            <aside className="hidden lg:block">
              <div className="panel sticky top-20 p-4">{FiltersBody}</div>
            </aside>

            <div>
              {/* Toolbar */}
              <div className="mb-4 flex flex-col gap-3 sm:flex-row sm:items-center">
                <div className="relative flex-1">
                  <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-surface-400" />
                  <input
                    type="search"
                    placeholder="Modelo, nombre o SKU…"
                    className="input-field-lg pl-10"
                    value={qInput}
                    onChange={(e) => setQInput(e.target.value)}
                  />
                </div>

                <div className="flex items-center gap-2">
                  <button
                    type="button"
                    className="btn-secondary gap-2 px-3 py-2.5 lg:hidden"
                    onClick={() => setFiltersOpen(true)}
                  >
                    <SlidersHorizontal className="h-4 w-4" />
                    Filtros
                    {activeFilters.length > 0 && (
                      <span className="flex h-5 min-w-5 items-center justify-center rounded-full bg-brand-600 px-1 text-[10px] font-bold text-white">
                        {activeFilters.length}
                      </span>
                    )}
                  </button>

                  <label className="relative hidden min-w-[180px] sm:block">
                    <span className="sr-only">Ordenar</span>
                    <ArrowUpDown className="pointer-events-none absolute left-3 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-surface-400" />
                    <select
                      className="input-field appearance-none py-2.5 pl-9 pr-8 text-sm font-medium"
                      value={orden}
                      onChange={(e) => setOrden(e.target.value as OrdenCatalogo)}
                    >
                      {ORDEN_OPTIONS.filter(
                        (o) => o.value !== 'relevancia' || !!qParam || !!qInput.trim(),
                      ).map((o) => (
                        <option key={o.value} value={o.value}>
                          {o.label}
                        </option>
                      ))}
                    </select>
                  </label>
                </div>
              </div>

              {/* Active filters */}
              {activeFilters.length > 0 && (
                <div className="mb-4 flex flex-wrap items-center gap-2">
                  {activeFilters.map((f) => (
                    <button
                      key={f.key}
                      type="button"
                      onClick={f.clear}
                      className="inline-flex items-center gap-1.5 rounded-full border border-brand-200 bg-brand-50 px-2.5 py-1 text-xs font-medium text-brand-800 transition-colors hover:bg-brand-100"
                    >
                      {f.label}
                      <X className="h-3 w-3" />
                    </button>
                  ))}
                  <button
                    type="button"
                    onClick={limpiarFiltros}
                    className="text-xs font-medium text-surface-500 hover:text-surface-800"
                  >
                    Limpiar todo
                  </button>
                </div>
              )}

              <div className="mb-4 flex items-center justify-between gap-3">
                <p className="text-sm text-surface-500">
                  {loading ? 'Buscando…' : (
                    <>
                      <span className="font-semibold tabular-nums text-surface-800">
                        {total.toLocaleString('es-MX')}
                      </span>{' '}
                      productos
                      {pages > 1 ? (
                        <span className="text-surface-400">
                          {' '}
                          · pág. {page}/{pages}
                        </span>
                      ) : null}
                      {ordenLabel ? (
                        <span className="text-surface-400"> · {ordenLabel}</span>
                      ) : null}
                    </>
                  )}
                </p>

                {/* Sort móvil compacto */}
                <label className="relative sm:hidden">
                  <select
                    className="input-field appearance-none py-2 pl-3 pr-8 text-xs font-medium"
                    value={orden}
                    onChange={(e) => setOrden(e.target.value as OrdenCatalogo)}
                    aria-label="Ordenar"
                  >
                    {ORDEN_OPTIONS.filter(
                      (o) => o.value !== 'relevancia' || !!qParam || !!qInput.trim(),
                    ).map((o) => (
                      <option key={o.value} value={o.value}>
                        {o.label}
                      </option>
                    ))}
                  </select>
                </label>
              </div>

              {/* Chips categoría móvil rápidos */}
              <div className="mb-5 flex gap-2 overflow-x-auto pb-1 lg:hidden">
                <Chip active={!categoria} onClick={() => filtrarCategoria('')}>
                  Todos
                </Chip>
                {CATEGORIAS_DEFAULT.map((cat) => (
                  <Chip
                    key={cat.slug}
                    active={categoria === cat.slug}
                    onClick={() => filtrarCategoria(cat.slug)}
                    className="shrink-0 capitalize"
                  >
                    {cat.nombre}
                  </Chip>
                ))}
              </div>

              {loading ? (
                <SkeletonGrid />
              ) : productos.length === 0 ? (
                <EmptyState
                  icon={<Package className="h-6 w-6" />}
                  title="Sin resultados"
                  description="Prueba otra categoría, quita filtros o cambia el término."
                />
              ) : (
                <>
                  <div className="grid grid-cols-2 gap-3 xl:grid-cols-3">
                    {productos.map((p) => (
                      <ProductCard key={productCatalogId(p)} product={p} />
                    ))}
                  </div>
                  <CatalogPagination
                    page={page}
                    pages={pages}
                    total={total}
                    limit={PAGE_SIZE}
                    onChange={goPage}
                  />
                </>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Sheet filtros móvil */}
      {filtersOpen && (
        <div className="fixed inset-0 z-[70] lg:hidden" role="dialog" aria-modal="true">
          <button
            type="button"
            className="absolute inset-0 bg-surface-950/40"
            aria-label="Cerrar filtros"
            onClick={() => setFiltersOpen(false)}
          />
          <div className="absolute inset-x-0 bottom-0 max-h-[85dvh] overflow-y-auto rounded-t-2xl bg-white p-4 pb-[max(1rem,env(safe-area-inset-bottom))] shadow-drawer animate-fade-in">
            <div className="mb-4 flex items-center justify-between">
              <h2 className="text-base font-semibold text-surface-900">Filtros</h2>
              <button
                type="button"
                className="icon-btn icon-btn-sm"
                onClick={() => setFiltersOpen(false)}
                aria-label="Cerrar"
              >
                <X className="h-4 w-4" />
              </button>
            </div>
            {FiltersBody}
            <button
              type="button"
              className="btn-primary mt-4 w-full"
              onClick={() => setFiltersOpen(false)}
            >
              Ver resultados
            </button>
          </div>
        </div>
      )}
    </>
  );
}
